// Форма создания действий цикла: ActionForm + ActionSituationModal,
// вывод черновика действия (understand/infer*), данные ACTION_TYPES/
// ACTION_GROUPS и связанные хелперы. Вынесено из CycleTab.tsx без
// изменения поведения (Фаза 1, разбор монолита).

import { useState, useEffect, useMemo, type FormEvent } from "react";
import {
  api,
  type CampaignSetup,
  type CycleActionType,
  type CycleActionVisibility,
  type GameCycle,
  type OfficeCode,
} from "../api";
import {
  compactText,
  GAME_UI_ASSET_BASE,
  Paragraphs,
  sameIds,
  readDraft,
  writeDraft,
  clearDraft,
  type Props,
} from "./shared";

type AfIconName =
  | "storage" | "route" | "report" | "shield" | "flask" | "nodes"
  | "chat" | "search" | "star" | "lock" | "target" | "wrench"
  | "spark" | "users" | "alert" | "cube" | "bulb" | "compass";

const AF_ICONS: Record<AfIconName, string> = {
  compass: '<circle cx="12" cy="12" r="9"/><path d="M15.5 8.5l-2.2 4.8-4.8 2.2 2.2-4.8z"/>',
  storage: '<rect x="3" y="8" width="18" height="12" rx="1"/><path d="M3 8l3-4h12l3 4M9 12h6"/>',
  route: '<path d="M3 7h10v8H3zM13 10h4l4 3v2h-8"/><circle cx="7" cy="17" r="1.7"/><circle cx="17" cy="17" r="1.7"/>',
  report: '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4h6v3H9zM8 11h8M8 15h5"/>',
  shield: '<path d="M12 3l7 3v5c0 4-3 7-7 9-4-2-7-5-7-9V6z"/>',
  flask: '<path d="M9 3h6M10 3v6l-5 9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1l-5-9V3"/><path d="M7.5 15h9"/>',
  nodes: '<circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="12" cy="18" r="2"/><path d="M7.5 7.6L11 16M16.5 7.6L13 16M8 6h8"/>',
  chat: '<path d="M4 5h16v10H9l-4 4V5z"/><path d="M8 9h8M8 12h5"/>',
  search: '<circle cx="10" cy="10" r="6"/><path d="M15 15l5 5"/>',
  star: '<path d="M12 3l2.6 6 6.4.5-4.9 4.2 1.5 6.3L12 16.9 6.4 20l1.5-6.3L3 9.5 9.4 9z"/>',
  lock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
  target: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.4"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>',
  wrench: '<path d="M15.5 4.5a5 5 0 0 0-6.4 6.2l-5.3 5.3 2.2 2.2 5.3-5.3a5 5 0 0 0 6.2-6.4l-2.7 2.7-2-2z"/>',
  spark: '<path d="M12 3v5M12 16v5M3 12h5M16 12h5M6 6l3 3M15 15l3 3M18 6l-3 3M9 15l-3 3"/>',
  users: '<circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.2 2.8-5 6-5s6 1.8 6 5"/><path d="M16 6a3 3 0 0 1 0 6M17 15c2 .4 4 2 4 5"/>',
  alert: '<path d="M12 4l9 16H3z"/><path d="M12 10v4M12 17h.01"/>',
  cube: '<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M4 7.5l8 4.5 8-4.5M12 12v9"/>',
  bulb: '<path d="M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10c-.8.8-1 1.4-1 2H9c0-.6-.2-1.2-1-2a6 6 0 0 1 4-10z"/>',
};

function AfIcon({ name }: { name: AfIconName }) {
  return (
    <svg
      className="cc-af-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: AF_ICONS[name] }}
    />
  );
}

function hookIcon(tone: ActionHook["tone"]): AfIconName {
  switch (tone) {
    case "danger":
      return "alert";
    case "resource":
      return "cube";
    case "politics":
      return "users";
    default:
      return "bulb";
  }
}

type ActionGroupId = "INTEL" | "DIPLOMACY" | "COMMAND" | "FREE";
interface ActionDraft {
  actionType: CycleActionType;
  visibility: CycleActionVisibility;
  targetCode: string;
  description: string;
  coActorUserIds: string[];
}
interface ActionTypeOption {
  code: CycleActionType;
  label: string;
  group: ActionGroupId;
  summary: string;
  useWhen: string;
  placeholder: string;
}
interface ActionGroupOption {
  id: ActionGroupId;
  title: string;
  subtitle: string;
  icon: string;
  subtypes: CycleActionType[];
}
interface ActionDraftUnderstanding {
  actionType: CycleActionType;
  targetCode: string;
  goal: string;
  method: string;
  involved: string[];
  roleFitLabel: string;
  possibleOutcome: string;
  risks: string[];
  needAlly: boolean;
}
interface LeverItem {
  icon: AfIconName;
  title: string;
  desc: string;
}
interface RoleLeverProfile {
  title: string;
  tagline: string;
  roleIcon: string;
  levers: LeverItem[];
  limits: LeverItem[];
}
interface ActionHook {
  title: string;
  text: string;
  tone?: "danger" | "knowledge" | "politics" | "resource";
}
const ACTION_TYPES: ActionTypeOption[] = [
  {
    code: "REQUEST_REPORT",
    label: "Получить официальные сведения",
    group: "INTEL",
    summary:
      "Запросить у выбранной службы подтверждённые данные, документы и позицию ответственных.",
    useWhen:
      "Вы знаете, у кого находятся сведения, и нужен официальный ответ, который можно использовать дальше.",
    placeholder:
      "Какие именно данные или документы вы запрашиваете, у кого и для какого решения?",
  },
  {
    code: "INVESTIGATE",
    label: "Выяснить скрытые причины",
    group: "INTEL",
    summary:
      "Самостоятельно собрать свидетельства, сопоставить версии и найти то, что не содержится в обычном отчёте.",
    useWhen:
      "Официальной информации недостаточно, источники противоречат друг другу или кто-то может скрывать важный факт.",
    placeholder:
      "Что именно вы проверяете, какие источники сопоставляете и какой факт хотите установить?",
  },
  {
    code: "CHECK_RUMOR",
    label: "Проверить конкретный слух",
    group: "INTEL",
    summary:
      "Подтвердить или опровергнуть одно неподтверждённое утверждение, которое уже ходит по городу.",
    useWhen:
      "Нужно понять, правда ли конкретный слух, прежде чем Совет начнёт на него реагировать.",
    placeholder: "Сформулируйте сам слух, его источник и способ проверки.",
  },
  {
    code: "MEET_FACTION",
    label: "Выслушать и склонить группу",
    group: "DIPLOMACY",
    summary:
      "Провести политическую встречу, понять интересы группы и попытаться изменить её позицию.",
    useWhen:
      "Важнее отношение и поддержка стороны, чем формальная сделка с точными взаимными обязательствами.",
    placeholder:
      "С кем вы встречаетесь, что хотите понять или изменить и какими доводами будете действовать?",
  },
  {
    code: "NEGOTIATE",
    label: "Предложить обмен условиями",
    group: "DIPLOMACY",
    summary:
      "Обсудить конкретную взаимную договорённость: что делает каждая сторона и на каких условиях.",
    useWhen:
      "У вас есть ясное предложение, встречное обязательство или компромисс, который можно зафиксировать.",
    placeholder:
      "Что вы предлагаете стороне, что просите взамен и какие условия считаете обязательными?",
  },
  {
    code: "PUBLIC_STATEMENT",
    label: "Обратиться к жителям публично",
    group: "DIPLOMACY",
    summary:
      "Открыто объяснить позицию Совета, успокоить, предупредить или мобилизовать общество.",
    useWhen:
      "Целью является широкая общественная реакция, а не решение одной службы или отдельной группы.",
    placeholder:
      "К кому обращаетесь, что сообщаете и какой публичной реакции хотите добиться?",
  },
  {
    code: "PRESSURE_INSTITUTION",
    label: "Поручить службе конкретную работу",
    group: "COMMAND",
    summary:
      "Использовать полномочия Совета, чтобы выбранная городская структура взяла на себя определённую задачу.",
    useWhen:
      "Нужно не получить сведения, а добиться от службы действия, решения или изменения порядка работы.",
    placeholder:
      "Какую конкретную работу должна выполнить служба, в каком порядке и какой результат вы ожидаете?",
  },
  {
    code: "FORCE_OPERATION",
    label: "Провести силовую операцию",
    group: "COMMAND",
    summary:
      "Защитить объект, пресечь нападение, оттеснить угрозу, задержать нарушителей или организовать контролируемое вторжение.",
    useWhen:
      "Нужно активное принудительное действие с риском эскалации, а не переговоры или обычное поручение службе.",
    placeholder:
      "Кого или что вы атакуете, защищаете, блокируете или задерживаете, какими силами и как снижаете риск эскалации?",
  },
  {
    code: "MOBILIZE_RESOURCE",
    label: "Организовать доступ к запасу",
    group: "COMMAND",
    summary:
      "Подготовить существующий городской запас, инфраструктуру или возможность к практическому использованию.",
    useWhen:
      "Нужно найти исполнителей, доставку, доступ или порядок применения уже имеющегося ресурса.",
    placeholder:
      "Какой запас или возможность вы хотите задействовать, кто это организует и для какой задачи?",
  },
  {
    code: "FREE_ACTION",
    label: "Свободное действие",
    group: "FREE",
    summary:
      "Описать нестандартный ход, который не укладывается в готовые поручения, переговоры или проверки.",
    useWhen:
      "Вы хотите сделать конкретный шаг по ситуации своими словами и доверить движку подобрать проверку, риски и последствия.",
    placeholder:
      "Опишите, что именно вы делаете, с кем или чем взаимодействуете, каким способом и какого результата ждёте.",
  },
];
const ACTION_GROUPS: ActionGroupOption[] = [
  {
    id: "INTEL",
    title: "Разведать",
    subtitle: "Официальные сведения, расследование, проверка слуха",
    icon: "action_type_intel.png",
    subtypes: ["REQUEST_REPORT", "INVESTIGATE", "CHECK_RUMOR"],
  },
  {
    id: "DIPLOMACY",
    title: "Договориться",
    subtitle: "Встречи с группами, обмен условиями, публичное обращение",
    icon: "action_type_diplomacy.png",
    subtypes: ["MEET_FACTION", "NEGOTIATE", "PUBLIC_STATEMENT"],
  },
  {
    id: "COMMAND",
    title: "Поручить службе",
    subtitle: "Поручение институту, операция, организация ресурса",
    icon: "action_type_command.png",
    subtypes: ["PRESSURE_INSTITUTION", "FORCE_OPERATION", "MOBILIZE_RESOURCE"],
  },
  {
    id: "FREE",
    title: "Свободное действие",
    subtitle: "Любой нестандартный ход по ситуации",
    icon: "action_type_free.png",
    subtypes: ["FREE_ACTION"],
  },
];
function ActionForm({ campaignId, setup, action }: Props) {
  const cycle = setup.cycle.current!;
  const key = `cc:${campaignId}:${cycle.id}:action`;
  const [draft, setDraft] = useState<ActionDraft>(() =>
    readDraft(key, {
      actionType: "FREE_ACTION",
      visibility: "PUBLIC",
      targetCode: "",
      description: "",
      coActorUserIds: [],
    }),
  );
  useEffect(() => writeDraft(key, draft), [key, draft]);
  const currentMember = setup.members.find((member) => member.isCurrentUser);
  const currentCharacter = currentMember
    ? setup.characters.find((character) => character.userId === currentMember.userId)
    : undefined;
  const roleProfile = roleLeverProfile(currentCharacter?.officeCode, currentCharacter?.publicTitle ?? setup.commandHub?.character.title);
  const hooks = useMemo(() => actionHooks(setup, cycle), [setup, cycle.id, cycle.title, cycle.situationSummary, cycle.centralProblem]);
  const understanding = useMemo(
    () => understandActionDraft(draft.description, currentCharacter?.officeCode, setup),
    [draft.description, currentCharacter?.officeCode, setup],
  );
  const progressByUserId = useMemo(
    () => new Map(cycle.memberProgress.map((item) => [item.userId, item])),
    [cycle.memberProgress],
  );
  useEffect(
    () =>
      setDraft((current) => {
        const available = current.coActorUserIds.filter(
          (id) => coActorAvailability(progressByUserId.get(id)).available,
        );
        return sameIds(available, current.coActorUserIds)
          ? current
          : { ...current, coActorUserIds: available };
      }),
    [progressByUserId],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!cycle.canAct || draft.description.trim().length < 20) return;
    const inferred = understandActionDraft(draft.description, currentCharacter?.officeCode, setup);
    const saved = await action(
      draft.coActorUserIds.length > 0
        ? "Отправляем совместное действие на согласование…"
        : "Действие принято. Игра покажет понятный итог и следующий шаг…",
      () =>
        api.performCycleAction(campaignId, {
          actionType: inferred.actionType,
          visibility: draft.visibility,
          targetCode: inferred.targetCode || undefined,
          description: draft.description,
          coActorUserIds: draft.coActorUserIds,
        }),
    );
    if (saved) {
      setDraft((current) => ({
        ...current,
        actionType: "FREE_ACTION",
        targetCode: "",
        description: "",
        coActorUserIds: [],
      }));
      clearDraft(key);
    }
  }

  return (
    <form className="cc-af" onSubmit={submit}>
      <section className="cc-af-card cc-af-role">
        <div className="cc-af-role-head">
          <span className="cc-af-medallion">
            <img src={`${GAME_UI_ASSET_BASE}/${roleProfile.roleIcon}`} alt="" width={46} height={46} />
          </span>
          <div className="cc-af-card-main">
            <small className="cc-af-eyebrow">Ваши рычаги</small>
            <strong>{roleProfile.title}</strong>
            <p>{roleProfile.tagline}</p>
          </div>
        </div>
        <ul className="cc-af-levers">
          {roleProfile.levers.map((lever) => (
            <li className="cc-af-lever" key={lever.title}>
              <span className="cc-af-lever-icon"><AfIcon name={lever.icon} /></span>
              <div><b>{lever.title}</b><span>{lever.desc}</span></div>
            </li>
          ))}
        </ul>
        {roleProfile.limits.length > 0 && (
          <ul className="cc-af-levers cc-af-levers-locked">
            {roleProfile.limits.map((limit) => (
              <li className="cc-af-lever locked" key={limit.title}>
                <span className="cc-af-lever-icon"><AfIcon name="lock" /></span>
                <div><b>{limit.title}</b><span>{limit.desc}</span></div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {hooks.length > 0 && (
        <section className="cc-af-card cc-af-hooks">
          <small className="cc-af-eyebrow">Зацепки дня</small>
          <div className="cc-af-hook-grid">
            {hooks.map((hook) => (
              <span className={`cc-af-hook ${hook.tone ?? "knowledge"}`} key={`${hook.title}-${hook.text}`} title={hook.text}>
                <AfIcon name={hookIcon(hook.tone)} />
                <b>{hook.title}</b>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="cc-af-card cc-af-write">
        <small className="cc-af-eyebrow">Что вы делаете?</small>
        <div className="cc-af-textarea">
          <textarea
            value={draft.description}
            onChange={(event) =>
              setDraft((current) => ({ ...current, description: event.target.value }))
            }
            placeholder="Опишите действие своими словами…"
            rows={5}
            maxLength={1200}
          />
          <span className="cc-af-counter">{draft.description.length}/1200</span>
        </div>
        <div className="cc-af-write-foot">
          <span className="cc-af-hint"><AfIcon name="chat" />Система сама поймёт смысл вашего действия и его возможные последствия.</span>
          <div className="cc-af-visibility" role="group" aria-label="Видимость действия">
            <button type="button" className={draft.visibility === "PUBLIC" ? "on" : ""} onClick={() => setDraft((current) => ({ ...current, visibility: "PUBLIC" }))}>Публично</button>
            <button type="button" className={draft.visibility === "PRIVATE" ? "on" : ""} onClick={() => setDraft((current) => ({ ...current, visibility: "PRIVATE" }))}>Лично</button>
          </div>
        </div>
      </section>

      <section className="cc-af-card cc-af-parse">
        <small className="cc-af-eyebrow">Игра поняла ход так</small>
        <ul className="cc-af-parse-list">
          <li><span className="cc-af-parse-key"><AfIcon name="target" />Цель</span><span className="cc-af-parse-val">{understanding.goal}</span></li>
          <li><span className="cc-af-parse-key"><AfIcon name="wrench" />Способ</span><span className="cc-af-parse-val">{understanding.method}</span></li>
          <li><span className="cc-af-parse-key"><AfIcon name="spark" />Может дать</span><span className="cc-af-parse-val">{understanding.possibleOutcome}</span></li>
          <li><span className="cc-af-parse-key"><AfIcon name="users" />Нужен ли союзник</span><span className="cc-af-parse-val">{draft.description.trim().length >= 20 ? (understanding.needAlly ? "Да — сильнее с профильным союзником" : "Нет — можно в одиночку") : "— будет определено из вашего описания"}</span></li>
        </ul>
        {understanding.risks.length > 0 && (
          <div className="cc-af-risks">
            {understanding.risks.map((risk) => <span key={risk}><AfIcon name="alert" />{risk}</span>)}
          </div>
        )}
      </section>

      <details className="cc-af-card cc-af-allies" open={understanding.needAlly}>
        <summary><AfIcon name="users" /><span>{understanding.needAlly ? "Это действие лучше вместе" : "Пригласить игрока в действие"}</span><span className="cc-af-chev" aria-hidden="true">›</span></summary>
        <div className="cc-af-ally-list">
          {setup.members
            .filter((member) => !member.isCurrentUser)
            .map((member) => {
              const availability = coActorAvailability(progressByUserId.get(member.userId));
              return (
                <label key={member.userId} className={availability.available ? "" : "disabled"}>
                  <input
                    type="checkbox"
                    disabled={!availability.available}
                    checked={availability.available && draft.coActorUserIds.includes(member.userId)}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        coActorUserIds: event.target.checked
                          ? [...current.coActorUserIds, member.userId]
                          : current.coActorUserIds.filter((id) => id !== member.userId),
                      }))
                    }
                  />
                  <span>
                    <b>{member.displayName}</b>
                    <small>{member.assignedOfficeTitle ?? "Участник Совета"} · {availability.text}</small>
                  </span>
                </label>
              );
            })}
        </div>
      </details>

      <div className="cc-af-submit">
        <button className="cc-af-send" disabled={!cycle.canAct || draft.description.trim().length < 20}>
          Отправить действие
        </button>
      </div>
    </form>
  );
}
function ActionSituationModal({ title, text, onClose }: { title: string; text: string; onClose: () => void }) {
  return (
    <div className="cc-modal-backdrop cc-action-modal-backdrop cc-guided-situation-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-action-modal-card cc-guided-situation-modal">
        <header className="cc-modal-header">
          <div>
            <span>Ситуация дня</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="cc-modal-close" onClick={onClose}>×</button>
        </header>
        <div className="cc-guided-situation-modal-body">
          <Paragraphs text={text} />
        </div>
      </div>
    </div>
  );
}
function roleLeverProfile(officeCode?: OfficeCode, fallbackTitle?: string): RoleLeverProfile {
  const title = fallbackTitle || "Представитель Совета";
  switch (officeCode) {
    case "RESOURCES_KEEPER":
    case "RESOURCES_AND_INFRASTRUCTURE":
    case "INFRASTRUCTURE_KEEPER":
      return {
        title,
        tagline: "Ваши решения влияют на обеспечение города.",
        roleIcon: "icon_world_institution_logistics.png",
        levers: [
          { icon: "storage", title: "Доступ к городским складам", desc: "Можете распределять запасы и резервы." },
          { icon: "route", title: "Контроль логистики", desc: "Определяете маршруты и приоритеты поставок." },
          { icon: "report", title: "Доступ к служебным отчётам", desc: "Получаете сводки и данные о потребностях." },
        ],
        limits: [
          { icon: "shield", title: "Службы безопасности", desc: "Требуют согласования со службой порядка." },
          { icon: "flask", title: "Научные проекты", desc: "Нужна координация с научным центром." },
        ],
      };
    case "ORDER_KEEPER":
    case "JUSTICE_KEEPER":
      return {
        title,
        tagline: "Вы отвечаете за порядок и безопасность.",
        roleIcon: "icon_world_institution_security.png",
        levers: [
          { icon: "shield", title: "Охрана и контроль доступа", desc: "Защита объектов и пресечение угроз." },
          { icon: "search", title: "Расследования", desc: "Проверка угроз и сбор доказательств." },
          { icon: "report", title: "Сводки о нарушениях", desc: "Данные о рисках и происшествиях." },
        ],
        limits: [
          { icon: "route", title: "Снабжение и производство", desc: "Требуют профильной службы." },
          { icon: "flask", title: "Научные проекты", desc: "Нужна координация с научным центром." },
        ],
      };
    case "KNOWLEDGE_KEEPER":
    case "INFORMATION_KEEPER":
      return {
        title,
        tagline: "Вы управляете знанием и информацией города.",
        roleIcon: "icon_dossier.png",
        levers: [
          { icon: "search", title: "Анализ данных", desc: "Сопоставление источников и фактов." },
          { icon: "flask", title: "Исследования", desc: "Профильные проекты и экспертиза." },
          { icon: "nodes", title: "Информационные каналы", desc: "Проверка фактов и распространение сведений." },
        ],
        limits: [
          { icon: "shield", title: "Силовые меры", desc: "Требуют службы порядка." },
          { icon: "route", title: "Распределение ресурсов", desc: "Требует службы снабжения." },
        ],
      };
    case "COORDINATOR":
      return {
        title,
        tagline: "Вы связываете службы и повестку Совета.",
        roleIcon: "icon_room_council.png",
        levers: [
          { icon: "nodes", title: "Координация служб", desc: "Согласование действий между сторонами." },
          { icon: "report", title: "Повестка Совета", desc: "Выносите вопросы на решение." },
          { icon: "star", title: "Публичная процедура", desc: "Официальные согласования и порядок." },
        ],
        limits: [
          { icon: "shield", title: "Полевые операции", desc: "Требуют профильных исполнителей." },
          { icon: "route", title: "Техническая работа", desc: "Требует профильных служб." },
        ],
      };
    case "COMMUNITIES_REPRESENTATIVE":
    case "FOREIGN_ENVOY":
    case "WELFARE_KEEPER":
      return {
        title,
        tagline: "Вы говорите от лица жителей и общин.",
        roleIcon: "icon_world_faction_citizens.png",
        levers: [
          { icon: "chat", title: "Переговоры", desc: "Влияете на позиции сторон." },
          { icon: "users", title: "Доверие жителей", desc: "Снятие конфликтов и поддержка общин." },
          { icon: "star", title: "Публичные обещания", desc: "Мобилизация и обращения к городу." },
        ],
        limits: [
          { icon: "report", title: "Приказы службам", desc: "Идут через Совет." },
          { icon: "shield", title: "Силовое давление", desc: "Может вызвать сопротивление." },
        ],
      };
    default:
      return {
        title,
        tagline: "Вы действуете от имени Совета.",
        roleIcon: "icon_room_council.png",
        levers: [
          { icon: "star", title: "Право действовать от Совета", desc: "Официальные запросы и поручения." },
          { icon: "chat", title: "Переговоры", desc: "Влияете на позиции сторон." },
          { icon: "spark", title: "Свободный ход", desc: "Нестандартное действие по ситуации." },
        ],
        limits: [
          { icon: "target", title: "Узкие задачи", desc: "Сильнее с профильной должностью." },
          { icon: "nodes", title: "Системные решения", desc: "Требуют решения Совета." },
        ],
      };
  }
}
function actionHooks(setup: CampaignSetup, cycle: GameCycle): ActionHook[] {
  const hooks: ActionHook[] = [];
  if (cycle.centralProblem) hooks.push({ title: "Главная проблема", text: compactText(cycle.centralProblem, 92), tone: "danger" });
  setup.commandHub?.risks.slice(0, 2).forEach((risk) => hooks.push({ title: risk.label, text: risk.severityLabel, tone: "danger" }));
  setup.world?.resources
    .filter((resource) => resource.currentValue <= Math.ceil(resource.maxValue * 0.6))
    .slice(0, 2)
    .forEach((resource) => hooks.push({ title: resource.displayName, text: `${resource.currentValue}/${resource.maxValue}`, tone: "resource" }));
  setup.world?.factions.slice(0, 2).forEach((faction) => hooks.push({ title: faction.displayName, text: compactText(faction.demands || faction.description, 70), tone: "politics" }));
  setup.visibleKnowledge.slice(0, 2).forEach((item) => hooks.push({ title: item.title, text: compactText(item.content || "Сведение можно использовать", 74), tone: "knowledge" }));
  return hooks.filter((item, index, all) => all.findIndex((candidate) => candidate.title === item.title) === index).slice(0, 6);
}
function understandActionDraft(text: string, officeCode: OfficeCode | undefined, setup: CampaignSetup): ActionDraftUnderstanding {
  const normalized = text.toLowerCase().replaceAll("ё", "е");
  const enough = normalized.trim().length >= 20;
  const actionType = inferActionType(normalized);
  const targetCode = inferActionTarget(actionType, normalized, setup);
  const needAlly = needsAllyForAction(actionType, officeCode, targetCode);
  return {
    actionType,
    targetCode,
    goal: enough ? inferGoal(text) : "будет определена из вашего описания",
    method: enough ? inferMethod(normalized, actionType) : "будет определён из вашего описания",
    involved: enough ? inferInvolved(actionType, targetCode, setup) : ["будет определено из вашего описания"],
    roleFitLabel: roleFitPreview(actionType, officeCode, needAlly),
    possibleOutcome: inferPossibleOutcome(actionType, normalized),
    risks: enough ? inferActionRisks(normalized, needAlly) : [],
    needAlly,
  };
}
function inferActionType(normalized: string): CycleActionType {
  if (!normalized.trim()) return "FREE_ACTION";
  if (/(узнать|выяснить|отчет|отчёт|данн|сведения|спросить|запрос)/.test(normalized)) return "REQUEST_REPORT";
  if (/(расслед|проверить|проверка|слух|кто стоит|выявить)/.test(normalized)) return "INVESTIGATE";
  if (/(договор|переговор|услов|компромисс|обеща|обмен)/.test(normalized)) return "NEGOTIATE";
  if (/(объявить|обращение|разъясн|успокоить|публичн)/.test(normalized)) return "PUBLIC_STATEMENT";
  if (/(охрана|патрул|рейд|задерж|защит|блокир|контроль доступа)/.test(normalized)) return "FORCE_OPERATION";
  if (/(запас|ресурс|склад|достав|логист|распредел|медикамент|материал|ремонт)/.test(normalized)) return "MOBILIZE_RESOURCE";
  if (/(служб|центр|совет|поручить|выделить|организовать группы)/.test(normalized)) return "PRESSURE_INSTITUTION";
  return "FREE_ACTION";
}
function inferActionTarget(type: CycleActionType, normalized: string, setup: CampaignSetup): string {
  const candidates = optionsForAction(type, setup);
  const matched = candidates.find((item) => normalized.includes(item.label.toLowerCase().replaceAll("ё", "е")) || normalized.includes(item.code.toLowerCase()));
  if (matched) return matched.code;
  if (type === "MOBILIZE_RESOURCE") {
    if (/(медик|лекар|аптеч)/.test(normalized)) return setup.world?.resources.find((item) => /медик|лекар/i.test(item.displayName))?.code ?? candidates[0]?.code ?? "";
    if (/(вода|очист|санитар)/.test(normalized)) return setup.world?.resources.find((item) => /вода|очист|санитар/i.test(item.displayName))?.code ?? candidates[0]?.code ?? "";
  }
  if (/(охран|поряд|патрул|рейд)/.test(normalized)) return setup.world?.institutions.find((item) => /охран|поряд|безопас/i.test(item.displayName))?.code ?? candidates[0]?.code ?? "";
  if (/(науч|исслед|вакцин|лаборатор)/.test(normalized)) return setup.world?.institutions.find((item) => /науч|исслед|знан/i.test(item.displayName))?.code ?? candidates[0]?.code ?? "";
  if (/(снаб|логист|склад|постав)/.test(normalized)) return setup.world?.institutions.find((item) => /снаб|логист|постав/i.test(item.displayName))?.code ?? candidates[0]?.code ?? "";
  return candidates[0]?.code ?? "";
}
function inferGoal(text: string): string {
  const first = text.trim().replace(/\s+/g, " ");
  return compactText(first, 110);
}
function inferMethod(normalized: string, type: CycleActionType): string {
  if (/(провер|аудит|инспек)/.test(normalized)) return "проверка и сбор подтверждений";
  if (/(договор|переговор|услов)/.test(normalized)) return "переговоры и обмен условиями";
  if (/(рейд|патрул|охрана|защит)/.test(normalized)) return "операция через охрану и контроль доступа";
  if (/(распредел|достав|склад|логист)/.test(normalized)) return "организация снабжения и маршрутов";
  return ACTION_TYPES.find((item) => item.code === type)?.label.toLowerCase() ?? "свободный ход";
}
function inferInvolved(type: CycleActionType, targetCode: string, setup: CampaignSetup): string[] {
  const target = optionsForAction(type, setup).find((item) => item.code === targetCode)?.label;
  const base = target ? [target] : [];
  if (type === "REQUEST_REPORT" || type === "INVESTIGATE") return [...base, "сведения"].slice(0, 3);
  if (type === "NEGOTIATE" || type === "MEET_FACTION") return [...base, "позиции сторон"].slice(0, 3);
  if (type === "MOBILIZE_RESOURCE") return [...base, "ресурсы"].slice(0, 3);
  if (type === "FORCE_OPERATION") return [...base, "риск эскалации"].slice(0, 3);
  return base.length ? base : ["участники ситуации"];
}
function needsAllyForAction(type: CycleActionType, officeCode?: OfficeCode, targetCode?: string): boolean {
  if (!officeCode) return false;
  const target = targetCode ?? "";
  if ((officeCode === "ORDER_KEEPER" || officeCode === "JUSTICE_KEEPER") && (type === "MOBILIZE_RESOURCE" || /SUPPLY|KNOWLEDGE/.test(target))) return true;
  if ((officeCode === "RESOURCES_AND_INFRASTRUCTURE" || officeCode === "RESOURCES_KEEPER" || officeCode === "INFRASTRUCTURE_KEEPER") && (type === "FORCE_OPERATION" || /ORDER/.test(target))) return true;
  if ((officeCode === "KNOWLEDGE_KEEPER" || officeCode === "INFORMATION_KEEPER") && (type === "FORCE_OPERATION" || type === "MOBILIZE_RESOURCE")) return true;
  return false;
}
function roleFitPreview(type: CycleActionType, officeCode: OfficeCode | undefined, needAlly: boolean): string {
  if (needAlly) return "Ваш персонаж может начать этот ход, но сильнее будет подключить профильного союзника.";
  if (!officeCode) return "Должность будет учтена при расчёте результата.";
  if (type === "FREE_ACTION") return "Свободный ход доступен всегда: мир оценит конкретику, риски и ваши полномочия.";
  return "Ваши полномочия будут учтены как один из факторов результата, но не заменят способ действия.";
}
function inferPossibleOutcome(type: CycleActionType, normalized: string): string {
  if (type === "REQUEST_REPORT" || type === "INVESTIGATE") return "сведение или зацепка для следующего шага";
  if (/(постоян|регламент|решение совета|выделить ресурсы)/.test(normalized)) return "подготовка решения Совета";
  if (type === "MOBILIZE_RESOURCE" || type === "FORCE_OPERATION") return "локальный эффект или препятствие";
  if (type === "NEGOTIATE" || type === "MEET_FACTION") return "условия сделки или новая позиция стороны";
  return "локальный эффект, сведение или подготовка";
}
function inferActionRisks(normalized: string, needAlly: boolean): string[] {
  const risks: string[] = [];
  if (needAlly) risks.push("может понадобиться профильный союзник");
  if (/(рейд|силов|забрать|изъять|давить|угрож)/.test(normalized)) risks.push("жёсткий метод может вызвать сопротивление");
  if (/(вакцин|исслед|производ)/.test(normalized)) risks.push("научные или производственные задачи могут требовать ресурсов и времени");
  if (/(общин|жител|населен)/.test(normalized)) risks.push("жители могут по-разному воспринять задержку или контроль");
  return risks.slice(0, 3);
}
function optionsForAction(type: CycleActionType, setup: CampaignSetup) {
  if (type === "MEET_FACTION" || type === "NEGOTIATE")
    return (
      setup.world?.factions.map((item) => ({
        code: item.code,
        label: item.displayName,
      })) ?? []
    );
  if (
    type === "REQUEST_REPORT" ||
    type === "INVESTIGATE" ||
    type === "PRESSURE_INSTITUTION" ||
    type === "FORCE_OPERATION"
  )
    return (
      setup.world?.institutions.map((item) => ({
        code: item.code,
        label: item.displayName,
      })) ?? []
    );
  if (type === "MOBILIZE_RESOURCE")
    return (
      setup.world?.resources.map((item) => ({
        code: item.code,
        label: item.displayName,
      })) ?? []
    );
  return [];
}
function coActorAvailability(progress?: GameCycle["memberProgress"][number]): {
  available: boolean;
  text: string;
} {
  if (!progress) return { available: false, text: "статус недоступен" };
  if (progress.dayReady) return { available: false, text: "завершил день" };
  const remaining = Math.max(0, progress.actionLimit - progress.actionsUsed);
  if (remaining <= 0) return { available: false, text: "действия закончились" };
  return { available: true, text: `доступно действий: ${remaining}` };
}

function actionTypeLabel(type: CycleActionType) {
  return ACTION_TYPES.find((item) => item.code === type)?.label ?? type;
}

export {
  AfIcon,
  actionTypeLabel,
  ACTION_TYPES,
  ACTION_GROUPS,
  ActionForm,
  ActionSituationModal,
  roleLeverProfile,
  actionHooks,
  understandActionDraft,
  inferActionType,
  inferActionTarget,
  inferGoal,
  inferMethod,
  inferInvolved,
  needsAllyForAction,
  roleFitPreview,
  inferPossibleOutcome,
  inferActionRisks,
  optionsForAction,
  coActorAvailability,
};
export type { ActionDraft, ActionDraftUnderstanding, ActionGroupId, ActionGroupOption, ActionHook, ActionTypeOption, RoleLeverProfile };
